// src/components/ResetPasswordForm.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const ResetPasswordForm = () => {
	const navigate = useNavigate();
	const location = useLocation(); // To get URL query parameters
	const [email, setEmail] = useState("");
	const [token, setToken] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [initialLoadError, setInitialLoadError] = useState(null);

	useEffect(() => {
		// Parse URL query parameters
		const params = new URLSearchParams(location.search);
		const emailParam = params.get("email");
		const tokenParam = params.get("token");

		if (emailParam && tokenParam) {
			setEmail(emailParam);
			setToken(tokenParam);
		} else {
			setInitialLoadError(
				"Invalid password reset link. Missing email or token."
			);
			toast.error("Invalid password reset link. Please request a new one.");
		}
	}, [location.search]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setInitialLoadError(null); // Clear any initial load errors

		if (newPassword !== confirmPassword) {
			toast.error("Passwords do not match.");
			setLoading(false);
			return;
		}

		if (newPassword.length < 8) {
			toast.error("Password must be at least 8 characters long.");
			setLoading(false);
			return;
		}

		try {
			const response = await axios.post(
				"https://parallex.approot.ng/reset_password.php", // Your PHP endpoint
				{
					email,
					token,
					password: newPassword,
					confirm_password: confirmPassword,
				},
				{
					headers: {
						"Content-Type": "application/json",
					},
				}
			);

			if (response.data.status) {
				toast.success(
					response.data.message || "Password has been reset successfully!"
				);
				navigate("/"); // Redirect to login page
			} else {
				toast.error(response.data.message || "Failed to reset password.");
				// If the error message indicates expired token, redirect to forgot password
				if (
					response.data.message &&
					response.data.message.toLowerCase().includes("reset link has expired")
				) {
					navigate("/forgot-password");
				}
			}
		} catch (error) {
			console.error("Reset password error:", error);
			toast.error("An error occurred during password reset. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	if (initialLoadError) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-100 font-inter'>
				<div className='max-w-md w-full bg-white p-8 rounded-lg shadow-lg text-center'>
					<h2 className='text-3xl font-semibold text-red-600 mb-4'>Error</h2>
					<p className='text-gray-700 mb-6'>{initialLoadError}</p>
					<button
						onClick={() => navigate("/forgot-password")}
						className='py-3 px-6 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500'
					>
						Request New Link
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen flex items-center justify-center bg-gray-100 font-inter'>
			<div className='max-w-md w-full bg-white p-8 rounded-lg shadow-lg'>
				<h2 className='text-3xl font-semibold text-center text-red-600 mb-6'>
					Reset Password
				</h2>

				<form onSubmit={handleSubmit} className='space-y-6'>
					<div>
						<label htmlFor='email' className='block text-gray-700 font-medium'>
							Email
						</label>
						<input
							type='email'
							id='email'
							value={email}
							className='w-full p-3 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-gray-100'
							readOnly // Email comes from URL, should not be editable
						/>
					</div>

					<div>
						<label
							htmlFor='newPassword'
							className='block text-gray-700 font-medium'
						>
							New Password
						</label>
						<input
							type='password'
							id='newPassword'
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							className='w-full p-3 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500'
							required
							minLength='8'
						/>
					</div>

					<div>
						<label
							htmlFor='confirmPassword'
							className='block text-gray-700 font-medium'
						>
							Confirm New Password
						</label>
						<input
							type='password'
							id='confirmPassword'
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							className='w-full p-3 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500'
							required
							minLength='8'
						/>
					</div>

					<button
						type='submit'
						disabled={loading}
						className='w-full py-3 mt-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed'
					>
						{loading ? "Resetting..." : "Reset Password"}
					</button>
				</form>
			</div>
		</div>
	);
};

export default ResetPasswordForm;
